import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';
import { useCatalog } from './apps-catalog';

const UserAppsContext = createContext();

export function UserAppsProvider({ children }) {
    const [activatedApps, setActivatedApps] = useState(() => new Set());
    const [loadingApps, setLoadingApps] = useState(true);
    const [userProfile, setUserProfile] = useState(() => ({
        first_name: '',
        last_name: '',
        context: '',
        trial_end_date: null,
        plan: null,
    }));
    const [userSettings, setUserSettings] = useState(() => ({
        navigation_mode: 'hover',
        dwell_time: 2000,
        cursor_speed: 3,
    }));
    const [loadingSettings, setLoadingSettings] = useState(true);

    const fetchWithAuth = useFetch();
    const { categories } = useCatalog();

    // Build route → applicationId lookup from catalog
    const routeToId = useMemo(() => {
        const map = {};
        for (const cat of categories) {
            for (const app of cat.apps) {
                if (app.route && app.id) map[app.route] = app.id;
            }
        }
        return map;
    }, [categories]);

    // Fetch user's activated apps on mount
    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoadingApps(true);
            try {
                const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/applications/me`);
                if (!response.ok) throw new Error(await response.text());
                const routes = await response.json();
                if (!cancelled) setActivatedApps(new Set(routes));
            } catch (e) {
                console.error('Failed to load user applications', e);
            } finally {
                if (!cancelled) setLoadingApps(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [fetchWithAuth]);

    // Fetch user settings on mount
    useEffect(() => {
        let cancelled = false;

        async function loadSettings() {
            setLoadingSettings(true);
            try {
                const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/applications/me/settings`);
                if (!response.ok) throw new Error(await response.text());
                const settings = await response.json();
                if (!cancelled) {
                    setUserSettings({
                        navigation_mode: settings.navigation_mode || 'hover',
                        dwell_time: settings.dwell_time || 2000,
                        cursor_speed: settings.cursor_speed || 3,
                    });
                }
            } catch (e) {
                console.error('Failed to load user settings', e);
                // Keep default settings on error
            } finally {
                if (!cancelled) setLoadingSettings(false);
            }
        }

        loadSettings();
        return () => { cancelled = true; };
    }, [fetchWithAuth]);

    const toggleApp = useCallback(async (route) => {
        const appId = routeToId[route];
        if (!appId) return;

        const isActive = activatedApps.has(route);
        const endpoint = isActive ? 'deactivate' : 'activate';

        // Optimistic update
        setActivatedApps((prev) => {
            const next = new Set(prev);
            if (isActive) next.delete(route);
            else next.add(route);
            return next;
        });

        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/applications/${appId}/${endpoint}`, { method: 'POST' });
            if (!response.ok) throw new Error(await response.text());
        } catch (e) {
            console.error(`Failed to ${endpoint} application`, e);
            // Revert on failure
            setActivatedApps((prev) => {
                const next = new Set(prev);
                if (isActive) next.add(route);
                else next.delete(route);
                return next;
            });
        }
    }, [routeToId, activatedApps, fetchWithAuth]);

    const subscribe = useCallback((planId) => {
        setUserProfile((prev) => ({
            ...prev,
            plan: planId,
            trial_end_date: null,
        }));
    }, []);

    const updateSettings = useCallback(async (newSettings) => {
        // Optimistic update
        const previousSettings = userSettings;
        setUserSettings((prev) => ({ ...prev, ...newSettings }));

        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/applications/me/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newSettings),
            });
            if (!response.ok) throw new Error(await response.text());
            return { success: true };
        } catch (e) {
            console.error('Failed to update user settings', e);
            // Revert on failure
            setUserSettings(previousSettings);
            return { success: false, error: e.message };
        }
    }, [userSettings, fetchWithAuth]);

    const value = useMemo(() => ({
        activatedApps,
        toggleApp,
        userProfile,
        subscribe,
        loadingApps,
        userSettings,
        updateSettings,
        loadingSettings,
    }), [activatedApps, toggleApp, userProfile, subscribe, loadingApps, userSettings, updateSettings, loadingSettings]);

    return (
        <UserAppsContext.Provider value={value}>
            {children}
        </UserAppsContext.Provider>
    );
}

export function useUserAppsContext() {
    return useContext(UserAppsContext);
}
